/**
 * src/components/ContactForm.js
 *
 * Support ticket creation form writes directly to Firestore.
 * Green/neutral palette, i18n support, mobile-first.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, RotateCcw, CheckCircle2 } from "lucide-react";
import supportService from "../services/supportService";
import { useAuth } from "../hooks/useAuth";
import { Button, Input, Textarea } from "./ui";
import { errorDev } from '../utils/logger.js';

export default function ContactForm() {
  const { t, ready: i18nReady } = useTranslation("support");
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [fieldErrors, setFieldErrors] = useState({});

  const safeT = useCallback(
    (key, fallback) => {
      if (!i18nReady) return fallback;
      try {
        const result = t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [t, i18nReady]
  );

  useEffect(() => {
    if (user) {
      if (user.displayName) setName((s) => s || user.displayName);
      if (user.email) setEmail((s) => s || user.email);
    }
  }, [user]);

  const categories = [
    { value: "general", label: safeT("contact_category_general", "General") },
    { value: "bug", label: safeT("contact_category_bug", "Bug Report") },
    { value: "account", label: safeT("contact_category_account", "Account Issue") },
    { value: "payment", label: safeT("contact_category_payment", "Payment") },
    { value: "listing", label: safeT("contact_category_listing", "Listing Issue") },
    { value: "other", label: safeT("contact_category_other", "Other") },
  ];

  const validate = useCallback(() => {
    const errors = {};

    if (!email) {
      errors.email = safeT("contact_error_email_required", "Email is required.");
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = safeT("contact_error_email", "Please provide a valid email address.");
    }

    if (!subject) {
      errors.subject = safeT("contact_error_subject", "Subject is required.");
    }

    if (!message) {
      errors.message = safeT("contact_error_message", "Message is required.");
    }

    setFieldErrors(errors);
    setError(null);

    if (Object.keys(errors).length > 0) {
      return false;
    }
    return true;
  }, [email, subject, message, safeT]);

  const reset = useCallback(() => {
    setName("");
    setEmail("");
    setSubject("");
    setCategory("general");
    setMessage("");
    setSuccess(null);
    setError(null);
    setFieldErrors({});
    if (user) {
      if (user.displayName) setName(user.displayName);
      if (user.email) setEmail(user.email);
    }
  }, [user]);

  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await supportService.createTicket(user, {
        name: name || "",
        email,
        subject,
        message,
        category,
      });
      setSuccess({
        id: res?.id || null,
        message: safeT("contact_success", "Ticket created. We'll email updates to you."),
      });
      reset();
    } catch (err) {
      setError(err?.message || "Failed to create ticket.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {safeT("contact_sign_in_prompt", "Sign in to open a support ticket.")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {/* Success */}
      {success && (
        <div className="flex items-start gap-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4" role="status">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">{success.message}</p>
            {success.id && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-mono">
                ID: {success.id}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300" role="alert">
          {error}
        </div>
      )}

      {/* Name */}
      <Input
        id="cf-name"
        label={safeT("contact_name", "Full name")}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={user?.displayName || ""}
      />

      {/* Email */}
      <Input
        id="cf-email"
        label={safeT("contact_email", "Email") + " *"}
        type="email"
        required
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          clearFieldError("email");
        }}
        error={fieldErrors.email}
        placeholder={user?.email || "you@example.com"}
      />

      {/* Subject */}
      <Input
        id="cf-subject"
        label={safeT("contact_subject", "Subject") + " *"}
        type="text"
        required
        value={subject}
        onChange={(e) => {
          setSubject(e.target.value);
          clearFieldError("subject");
        }}
        error={fieldErrors.subject}
        placeholder={safeT("contact_subject_placeholder", "Brief description of the issue")}
      />

      {/* Category */}
      <div>
        <label htmlFor="cf-category" className="block text-sm font-medium text-muted-foreground mb-1">
          {safeT("contact_category", "Category")}
        </label>
        <select
          id="cf-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="ui-input"
        >
          {categories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {/* Message */}
      <Textarea
        id="cf-message"
        label={safeT("contact_message", "Message") + " *"}
        required
        rows={4}
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
          clearFieldError("message");
        }}
        error={fieldErrors.message}
        placeholder={safeT("contact_message_placeholder", "Describe the issue and what you expected to happen.")}
      />

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="flex-1"
        >
          <>
            <Send className="w-4 h-4" />
            {loading
              ? safeT("contact_sending", "Sending…")
              : safeT("contact_send", "Send")}
          </>
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={reset}
          disabled={loading}
          className="flex-1"
        >
          <>
            <RotateCcw className="w-4 h-4" />
            {safeT("contact_reset", "Reset")}
          </>
        </Button>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        {safeT("response_time", "We respond within 24–72 hours.")}
      </p>
    </form>
  );
}
